import {
  Box,
  Card,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { TabPanelProps } from "~/components/GenericTab";
import useMapPopup from "~/pages/MapPage/hooks/useMapPopup";
import { useMapStyles } from "~/pages/MapPage/MapPage";
import MapPopups from "~/pages/MapPage/popups/MapPopups";
import { CustomSlider, useSlider } from "~/utils/hooks/useSlider";
import { isNumber } from "~/utils/isNumber";
import { CheckBoxFormControlLabel } from "../components/CheckBoxFormControlLabel";
import { ProjectProgressFilterContainer } from "../components/ProjectProgressMapFilters";
import { usePoiItemDraw, usePoiItems } from "../hooks/usePoiItems";
import { useProjectDetails } from "../hooks/useProjectDetails";
import { ColorOptionEnum, useProjectMap } from "../hooks/useProjectMap";
import { useProjectMapOptions } from "../hooks/useProjectMapOptions";
import {
  CheckBoxItems,
  GeojsonAttributes,
  useProjectShapeFile,
} from "../hooks/useProjectShapeFile";
import { MapTabs } from "./MapTabs";
import { OpacitySlider } from "./OpacitySlider";
import { PoiItemsContainer } from "./PoiItemsContainer";

// type ProjectMapContentProps = {

// };
export const MapContentTabPanel = React.memo(function MapContentTabPanel(
  props: TabPanelProps,
): JSX.Element {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`nav-tabpanel-${index}`}
      aria-labelledby={`nav-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </Box>
  );
});
export type MapContentTypes = "poi-items" | "map-control";

export type CheckBoxOptionKeys = "areas" | "vessels" | "planning";

type ProjectMapContentProps = {
  targetRef?: React.MutableRefObject<HTMLElement | undefined>;

  wprView?: boolean;
};

export const ProjectMapContent = ({
  targetRef,
  wprView = false,
}: ProjectMapContentProps = {}) => {
  const { id } = useProjectDetails();
  const [drawModeEnabled, setDrawModeEnabled] = React.useState<boolean>(false);

  const { setSliderValue, sliderValue } = useSlider();

  const { setSliderValue: setProgressOpacity, sliderValue: progressOpacity } =
    useSlider({ initialValue: 1 });

  const { setSliderValue: setPlanningOpacity, sliderValue: planningOpacity } =
    useSlider({ initialValue: 0 });

  // projectId tulee routerista, joten se halutaan passata projectShapehookille
  // sillä jos myöhemmin halutaan käyttää hookkia esim pääkartalla ei hookki voi käyttää routterista saamia tietoja
  const {
    vessels,

    hasPlan,
    hasProgress,
    sliderMarks,
    sliderMax,
    mapItemColors,
    progressLoading,
  } = useProjectShapeFile({
    id,
  });
  const [currentTab, setCurrentTab] =
    React.useState<MapContentTypes>("map-control");
  const poiTabActive = currentTab === "poi-items";
  const mapClasses = useMapStyles();
  const { createPopup, unmountPopup, selectedItem, multipleItems } =
    useMapPopup({
      disablePopups: drawModeEnabled,
    });

  const { t } = useTranslation();
  const {
    areaOptions,
    selectedColorOption,
    vesselOptions,
    setSelectedColorOption,
    setVesselOptions,
    setAreaOptions,
    showPlanning,
    setShowPlanning,
    setShowPoiMarkers,
    showPoiMarkers,
    checkBoxAreas,
    projectMapOptions,
  } = useProjectMapOptions({ id });

  const { map } = useProjectMap({
    sliderValue,
    id,
    progressOpacity: progressOpacity as number,
    planningOpacity: planningOpacity as number,
    options: projectMapOptions,
    wprView,
  });

  const { poiItems } = usePoiItems({ id });

  usePoiItemDraw({
    enabled: showPoiMarkers || drawModeEnabled || poiTabActive,
    map,
    markers: poiItems,
    toggleAnimation: showPoiMarkers,
    disableAnimation: wprView,
  });

  React.useEffect(() => {
    if (map) {
      createPopup({ map });
    }
    return () => {
      unmountPopup();
    };
  }, [createPopup, map, unmountPopup]);
  // TODO label tiettyihin väleihin?

  const handleOptions = React.useCallback(
    (
      optionArray: CheckBoxItems,
      setOptions: (value: React.SetStateAction<CheckBoxItems>) => void,
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const item = event.currentTarget.name;
      if (
        !optionArray?.includes(event.currentTarget.value) &&
        event.currentTarget.checked
      )
        setOptions((prevOps) =>
          prevOps === undefined ? [item] : [...prevOps, item],
        );
      else
        setOptions((prevOps) =>
          prevOps === undefined ? [] : [...prevOps.filter((v) => v !== item)],
        );
    },
    [],
  );

  // const handleCheckBoxChange = React.useCallback(
  //   (optionType: CheckBoxOptionKeys) => {
  //     return (event: React.ChangeEvent<HTMLInputElement>) => {
  //       switch (optionType) {
  //         case "vessels":
  //           if (!vesselOptions) return;
  //           handleOptions(vesselOptions, setVesselOptions, event);
  //           break;
  //         case "areas":
  //           handleOptions(areaOptions, setAreaOptions, event);
  //           break;
  //         default:
  //           break;
  //       }
  //     };
  //   },
  //   [
  //     areaOptions,
  //     handleOptions,
  //     setAreaOptions,
  //     setVesselOptions,
  //     vesselOptions,
  //   ],
  // );

  const handleVesselCheckBoxChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleOptions(vesselOptions, setVesselOptions, event);
    },

    [handleOptions, setVesselOptions, vesselOptions],
  );

  const handleAreaCheckBoxChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleOptions(areaOptions, setAreaOptions, event);
    },

    [handleOptions, setAreaOptions, areaOptions],
  );
  const handleSelectChange = React.useCallback(
    (event: SelectChangeEvent) => {
      setSelectedColorOption(event.target.value as keyof GeojsonAttributes);
    },
    [setSelectedColorOption],
  );

  const menuItems = React.useMemo(
    () =>
      Object.keys(ColorOptionEnum).map((option) => {
        if (isNumber(option)) return null;
        return (
          <MenuItem key={option} value={option}>
            {t(`color-option-enum:${option}`)}
          </MenuItem>
        );
      }),
    [t],
  );

  // remove default controlls from sc map
  React.useEffect(() => {
    if (!wprView || !map) return;
    const controls = map.getControls();
    controls.clear();
  }, [map, wprView]);

  return (
    <Box
      // ref={mapRef}
      sx={{ position: "relative" }}
    >
      <Box
        sx={{
          display: "flex",
          height: { xs: "450px", md: "600px", xl: "650px" },
        }}
        id="mapContainer"
        ref={targetRef}
      >
        <Card id="popup" className={mapClasses.popup}>
          {map && (
            <MapPopups
              map={map}
              selectedItem={selectedItem}
              multipleItems={multipleItems}
            />
          )}
        </Card>
      </Box>
      <MapTabs currentTab={currentTab} setCurrentTab={setCurrentTab} />
      <Divider />
      <Paper
        sx={{
          marginTop: "10px",
          display: "grid",
          padding: "10px",
        }}
      >
        <MapContentTabPanel index="map-control" value={currentTab}>
          <ProjectProgressFilterContainer
            progressLoading={progressLoading}
            hasProgressGeoJson={hasProgress}
          >
            <Box sx={{ padding: "5px" }}>
              <Box sx={{ mt: "10px", paddingX: "5px" }}>
                <Typography>{t("projects:map-slider")}</Typography>
                <CustomSlider
                  setSliderValue={setSliderValue}
                  sliderValue={sliderValue}
                  customMarks={sliderMarks}
                  step={null}
                  max={sliderMax}
                  disabled={!hasProgress}
                  wprView={wprView}
                />
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "4fr 3fr",
                  // paddingX: "25px",
                }}
              >
                <FormGroup>
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <FormControlLabel
                        labelPlacement="end"
                        label={
                          <Typography variant="h5">
                            {t("projects:areas")}
                          </Typography>
                        }
                        control={
                          <Checkbox
                            //     name={v}
                            color={
                              selectedColorOption === "SubArea"
                                ? "info"
                                : "primary"
                            }
                            onChange={(e) =>
                              e.currentTarget.checked
                                ? setAreaOptions(checkBoxAreas)
                                : setAreaOptions([])
                            }
                            defaultChecked
                          />
                        }
                      />
                    </Box>

                    <CheckBoxFormControlLabel
                      checkedOptions={areaOptions}
                      checkBoxItems={checkBoxAreas}
                      selectedColorOption={selectedColorOption}
                      colorOption="SubArea"
                      handleCheckBoxChange={handleAreaCheckBoxChange}
                      // handleCheckBoxChange={handleCheckBoxChange("areas")}
                      colorPalette={mapItemColors}
                    />
                  </Box>
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <FormControlLabel
                        labelPlacement="end"
                        label={
                          <Typography variant="h5">
                            {t("map:vessels")}
                          </Typography>
                        }
                        control={
                          <Checkbox
                            color={
                              selectedColorOption === "Vessel"
                                ? "info"
                                : "primary"
                            }
                            //     name={v}
                            onChange={(e) =>
                              e.currentTarget.checked
                                ? setVesselOptions(vessels)
                                : setVesselOptions([])
                            }
                            defaultChecked
                          />
                        }
                      />
                    </Box>

                    <CheckBoxFormControlLabel
                      checkedOptions={vesselOptions}
                      checkBoxItems={vessels}
                      selectedColorOption={selectedColorOption}
                      colorOption="Vessel"
                      handleCheckBoxChange={handleVesselCheckBoxChange}
                      // handleCheckBoxChange={handleCheckBoxChange("vessels")}
                      colorPalette={mapItemColors}
                    />
                  </Box>
                </FormGroup>

                <Box
                  sx={{
                    display: "grid",
                    height: "min-content",
                    gap: "5px",
                  }}
                >
                  <Typography variant="h5">
                    {t("projects:otherOptions")}
                  </Typography>
                  {hasProgress && (
                    <FormControl fullWidth>
                      <InputLabel>{t("projects:colorBy")}</InputLabel>
                      <Select
                        value={selectedColorOption}
                        onChange={handleSelectChange}
                      >
                        {menuItems}
                      </Select>
                    </FormControl>
                  )}
                  <Tooltip
                    title={hasPlan ? "" : t("projects:planTooltip") ?? ""}
                  >
                    <FormControlLabel
                      label={t("projects:planning")}
                      sx={{ marginRight: "auto" }}
                      control={
                        <Switch
                          onChange={(e) =>
                            setShowPlanning(e.currentTarget.checked)
                          }
                          disabled={!hasPlan}
                          color="warning"
                          checked={showPlanning}
                        />
                      }
                    />
                  </Tooltip>

                  {poiItems && poiItems?.length > 0 && (
                    <FormControlLabel
                      label={t("projects:poi-Markers")}
                      sx={{ marginRight: "auto" }}
                      control={
                        <Switch
                          onChange={(e) =>
                            setShowPoiMarkers(e.currentTarget.checked)
                          }
                          color="info"
                          checked={showPoiMarkers}
                        />
                      }
                    />
                  )}

                  <OpacitySlider
                    sliderValue={progressOpacity}
                    setSliderValue={setProgressOpacity}
                  />

                  <OpacitySlider
                    sliderValue={planningOpacity}
                    setSliderValue={setPlanningOpacity}
                    isPlanning
                    isDisabled={!hasPlan}
                  />
                </Box>
              </Box>
            </Box>
          </ProjectProgressFilterContainer>
        </MapContentTabPanel>
        <MapContentTabPanel index="poi-items" value={currentTab}>
          <PoiItemsContainer
            setDrawModeEnabled={setDrawModeEnabled}
            map={map}
          />
        </MapContentTabPanel>
      </Paper>
    </Box>
  );
};
